// src/pages/Marketing.jsx
import React from "react";
import {
  FaCheckCircle,
  FaCogs,
  FaAward,
  FaLightbulb,
  FaSchool,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { COLORS } from "../constants/colors";

const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const Marketing = () => {
  return (
    <div className="bg-gray-50">
      {/* Hero Section */}
      <div
        className="relative h-[400px] sm:h-[600px] flex items-center justify-start bg-cover bg-center px-6 sm:px-16 lg:px-32"
        style={{
          backgroundImage: "url('/assets/SoftCoders.jpg')",
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-60" />
        <div
          className="absolute inset-0"
          style={{ background: COLORS.gradientHero, opacity: 0.1 }}
        />
        <motion.div
          className="relative z-10 text-left"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
             <h1 className="text-3xl sm:text-5xl md:text-7xl font-bold text-white drop-shadow-md">
            About Soft Coders
          </h1>
          <p
            className="font-semibold text-xl text-white tracking-wider mb-4 sm:mb-6 pt-5"
         
          >
            We Are The Best Web & Software Development Firm In Your City
          </p>
         
        </motion.div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-16 px-6 lg:px-8 space-y-16">
        {/* Who We Are */}
        <motion.section
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="bg-white rounded-2xl p-8 sm:p-12 shadow-xl border-t-4"
          style={{ borderColor: COLORS.primary }}
        >
          <h2
            className="text-3xl sm:text-4xl font-semibold mb-6"
            style={{ color: COLORS.primaryDark }}
          >
            Who We Are
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-gray-700 leading-relaxed mb-6 text-base sm:text-lg">
                Softcoders has been a trusted name in the IT industry for years.
                We deliver high-quality web design, custom software, and digital
                solutions to help our clients grow their businesses efficiently
                and creatively.
              </p>
              <ul className="space-y-3">
                {[
                  "Reasonable Cost",
                  "Quality Ensured",
                  "Responsive Design",
                  "24x7 Support",
                ].map((text, i) => (
                  <motion.li
                    key={i}
                    className="flex items-center text-gray-800"
                    whileHover={{ x: 5 }}
                    transition={{ type: "spring", stiffness: 200 }}
                  >
                    <FaCheckCircle
                      className="text-xl sm:text-2xl mr-3"
                      style={{ color: COLORS.primary }}
                    />
                    <span className="text-base sm:text-lg">{text}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
            <motion.img
              src="/assets/officepic.png"
              alt="Team working"
              className="rounded-xl shadow-lg hidden md:block"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </motion.section>

        {/* Gurukul ERP Partnership */}
        <motion.section
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="bg-white rounded-2xl p-8 sm:p-12 shadow-xl border-t-4"
          style={{ borderColor: COLORS.primary }}
        >
          <h2
            className="text-3xl sm:text-4xl font-semibold mb-10 text-center"
            style={{ color: COLORS.primaryDark }}
          >
            Our Partnership with Gurukul ERP
          </h2>
          <div className="flex flex-col md:flex-row justify-center">
            {[
              {
                icon: <FaSchool />,
                title: "Streamlined School Management",
                text: "We empower educational institutions by streamlining operations and enhancing efficiency.",
              },
              {
                icon: <FaCheckCircle />,
                title: "Automated Administrative Workflows",
                text: "Our solutions automate routine tasks, allowing educators to focus on teaching.",
              },
              {
                icon: <FaLightbulb />,
                title: "Enhanced Communication Channels",
                text: "We foster better communication between staff, students, and parents for improved engagement.",
              },
            ].map((card, i) => (
              <motion.div
                key={i}
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
                className="flex flex-col items-center bg-blue-50 rounded-xl p-6 sm:p-8 shadow-md m-3"
              >
                <div
                  className="text-5xl sm:text-6xl mb-4"
                  style={{ color: COLORS.primary }}
                >
                  {card.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2">
                  {card.title}
                </h3>
                <p className="text-gray-700 text-center text-sm sm:text-base">
                  {card.text}
                </p>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/contact"
              className="px-8 py-3 rounded-lg shadow-lg text-white font-semibold"
              style={{
                backgroundColor: COLORS.primary,
              }}
            >
              Learn More About Gurukul ERP
            </Link>
          </div>
        </motion.section>

        <motion.section
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
         viewport={{ once: false, amount: 0.2 }}

          className="bg-white rounded-2xl p-8 sm:p-12 shadow-xl border-t-4"
          style={{ borderColor: COLORS.primary }}
        >
          <h2
            className="text-3xl sm:text-4xl font-semibold mb-10"
            style={{ color: COLORS.primaryDark }}
          >
            Why Choose Us?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
            {[
              {
                icon: <FaCogs />,
                title: "Years of Experience",
                desc: "We have a proven track record delivering top-tier IT solutions across diverse industries.",
              },
              {
                icon: <FaAward />,
                title: "Customer Satisfaction",
                desc: "We deliver results that exceed expectations, ensuring every client’s success.",
              },
              {
                icon: <FaLightbulb />,
                title: "Creative & Responsive Design",
                desc: "Our designs are modern, responsive, and built for performance.",
              },
              {
                icon: <FaCheckCircle />,
                title: "Innovative Technology",
                desc: "We stay ahead of trends to build future-ready applications.",
              },
              {
                icon: <FaAward />,
                title: "Dedication to Excellence",
                desc: "Every project we deliver reflects precision, innovation, and passion.",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ duration: 0.3 }}
                className="flex flex-col items-center text-center bg-gray-50 p-6 rounded-xl shadow-sm hover:shadow-lg"
              >
                <div
                  className="text-5xl sm:text-6xl mb-4"
                  style={{ color: COLORS.primary }}
                >
                  {item.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-700 text-sm sm:text-base">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.section>
      </main>
    </div>
  );
};

export default Marketing;
